import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Tracks scroll progress [0, 1] of a section element relative to the viewport.
 * Uses passive scroll listener + requestAnimationFrame throttle.
 *
 * Progress = 0 when the section top reaches the viewport top.
 * Progress = 1 when the section bottom reaches the viewport bottom.
 */
export function useScrollProgress(): {
  ref: React.RefObject<HTMLElement | null>
  progress: number
} {
  const ref = useRef<HTMLElement | null>(null)
  const [progress, setProgress] = useState(0)
  const rafId = useRef<number>(0)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const scrollableDistance = rect.height - window.innerHeight

    if (scrollableDistance <= 0) {
      setProgress(0)
      return
    }

    // rect.top starts positive (below viewport top), goes negative as we scroll down
    const scrolled = -rect.top
    const raw = scrolled / scrollableDistance
    setProgress(Math.max(0, Math.min(1, raw)))
  }, [])

  useEffect(() => {
    function onScroll() {
      if (rafId.current) return
      rafId.current = requestAnimationFrame(() => {
        update()
        rafId.current = 0
      })
    }

    window.addEventListener("scroll", onScroll, { passive: true })

    // Initial measurement via rAF to avoid sync setState in effect
    rafId.current = requestAnimationFrame(() => {
      update()
      rafId.current = 0
    })

    return () => {
      window.removeEventListener("scroll", onScroll)
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [update])

  return { ref, progress }
}
